import { useState } from 'react';
import { GlobalSearch, Trash } from 'iconsax-react';

import ConfirmationDeleteDialog from '@shared/components/reusables/confrimation-delete-dialog';

import { useKnowledgeBaseFilters } from '../hooks/use-knowledge-base-filters';
import {
  WEBSITE_MODES,
  knowledgeWebsitesApi,
  type KnowledgeWebsite,
} from '../services/knowledge-content-api';
import { formatKnowledgeDate } from '../utils/format-date';
import { displayUrl } from '../utils/labels';
import { ContentCard } from './content-card';
import { ContentList } from './content-list';
import { ContentToolbar } from './content-toolbar';
import { WebsiteDialog } from './website-dialog';

export function WebsitesTab({
  kbaseId,
  softActions = false,
}: {
  kbaseId: number;
  softActions?: boolean;
}) {
  const { search } = useKnowledgeBaseFilters();
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<KnowledgeWebsite | null>(null);

  const list = knowledgeWebsitesApi.useFetchInfinite({
    kbase: kbaseId,
    ...(search && { q: search }),
    page_size: 25,
  });
  const remove = knowledgeWebsitesApi.useDelete();
  const update = knowledgeWebsitesApi.useUpdate();

  return (
    <>
      <ContentToolbar
        placeholder={
          softActions ? 'Search in Web Crawler' : 'Search in your urls'
        }
        addLabel='Add URL'
        addVariant={softActions ? 'primaryPale' : 'primary'}
        onAdd={() => setAdding(true)}
      />
      <ContentList
        query={list}
        searching={Boolean(search)}
        emptyIcon={<GlobalSearch size={24} />}
        emptyTitle='No URLs yet'
        emptyDescription='Point the crawler at your site and its pages join the knowledge base.'
        renderItem={(website) => {
          const mode = WEBSITE_MODES.find(
            (option) => option.value === website.mode
          )?.label;
          return (
            <ContentCard
              key={website.id}
              data-website-id={website.id}
              title={displayUrl(website.url)}
              badge={
                !website.is_active ? (
                  <span className='rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500'>
                    Paused
                  </span>
                ) : undefined
              }
              meta={
                <>
                  {formatKnowledgeDate(website.created_at)}
                  {mode ? ` · ${mode}` : ''}
                </>
              }
              menu={[
                {
                  label: website.is_active
                    ? 'Pause crawling'
                    : 'Resume crawling',
                  disabled: update.isPending,
                  onSelect: () =>
                    update.mutate({
                      id: website.id,
                      is_active: !website.is_active,
                    }),
                },
                {
                  label: 'Delete',
                  icon: (
                    <Trash
                      size={16}
                      className='text-red-500'
                    />
                  ),
                  destructive: true,
                  onSelect: () => setDeleting(website),
                },
              ]}
            />
          );
        }}
      />

      <WebsiteDialog
        kbaseId={kbaseId}
        open={adding}
        onOpenChange={setAdding}
      />
      <ConfirmationDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        cancelHandler={() => setDeleting(null)}
        confirmationHandler={() => {
          if (!deleting) return;
          remove.mutate(
            { id: deleting.id },
            { onSuccess: () => setDeleting(null) }
          );
        }}
      />
    </>
  );
}
